import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Card, Descriptions, Divider, Empty, Input, List, Modal, Space, Spin, Tag, Typography, message,
} from "antd";
import {
  ApartmentOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined,
  ReloadOutlined, UndoOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { fetchTaskDetail } from "../qv4/models/shared/taskDetailModel";

export const OPERATIONS_APPROVAL_META = {
  operations_v2_authorize_fulfillment: { label: "Operations Handover", color: "geekblue" },
  operations_v2_purchase_price_variance: { label: "Purchase Price Variance", color: "gold" },
  operations_v2_customer_warranty_resolution: { label: "Warranty Resolution", color: "purple" },
  operations_v2_residual_inventory_disposition: { label: "Residual Inventory Decision", color: "cyan" },
  operations_v2_customer_change_refund_adjustment: { label: "Refund Adjustment", color: "volcano" },
};

const money = value => value == null ? "—" : `AED ${Number(value || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = value => String(value || "—").replaceAll("_", " ").replace(/\b\w/g, char => char.toUpperCase());
const statusForAction = action => action === "approve" ? "approved" : action === "reject" ? "rejected" : "changes_requested";

function addressText(address) {
  if (!address) return "No address snapshot";
  return [address.name, address.type, address.address].filter(Boolean).join(" · ") || "Address details unavailable";
}

function PayloadDetails({ actionKey, payload }) {
  if (actionKey === "operations_v2_purchase_price_variance") {
    return (
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Operations order">#{payload.operations_order_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Procurement attempt">#{payload.procurement_attempt_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Version">{payload.subject_version ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Proposed prices">{Array.isArray(payload.prices) ? `${payload.prices.length} price entries` : "—"}</Descriptions.Item>
        <Descriptions.Item label="Notes" span={2}>{payload.notes || "—"}</Descriptions.Item>
      </Descriptions>
    );
  }
  if (actionKey === "operations_v2_customer_warranty_resolution") {
    return (
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Operations order">#{payload.operations_order_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Warranty case">#{payload.warranty_case_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Resolution">{humanize(payload.resolution)}</Descriptions.Item>
        <Descriptions.Item label="Amount">{money(payload.amount)}</Descriptions.Item>
        <Descriptions.Item label="Reason" span={2}>{payload.reason || "—"}</Descriptions.Item>
      </Descriptions>
    );
  }
  if (actionKey === "operations_v2_residual_inventory_disposition") {
    return (
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Operations order">#{payload.operations_order_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Residual inventory">#{payload.residual_inventory_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Disposition">{humanize(payload.disposition)}</Descriptions.Item>
        <Descriptions.Item label="Version">{payload.subject_version ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Notes" span={2}>{payload.notes || payload.reason || "—"}</Descriptions.Item>
      </Descriptions>
    );
  }
  if (actionKey === "operations_v2_customer_change_refund_adjustment") {
    return (
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Operations order">#{payload.operations_order_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Change request">#{payload.change_request_id ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Calculated refund">{money(payload.calculated_refund_amount)}</Descriptions.Item>
        <Descriptions.Item label="Proposed refund">{money(payload.approved_refund_amount)}</Descriptions.Item>
        <Descriptions.Item label="Reason" span={2}>{payload.reason || "—"}</Descriptions.Item>
      </Descriptions>
    );
  }
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No additional request details" />;
}

export function OperationsApprovalModal({ open, approval, userId, userMap, onClose, onDone }) {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const payload = approval?.modified_payload ?? approval?.payload ?? {};
  const meta = OPERATIONS_APPROVAL_META[approval?.action_key] ?? { label: "Operations Approval", color: "geekblue" };
  const isHandover = approval?.action_key === "operations_v2_authorize_fulfillment";
  const [projection, setProjection] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [approvedNeedsApply, setApprovedNeedsApply] = useState(false);

  const loadHandover = async () => {
    if (!isHandover || !approval?.query_pk) return;
    setLoading(true);
    setLoadError("");
    try {
      const [projectionResult, taskResult] = await Promise.all([
        axios.get(`${API_DOMAIN}/operations-v2/handovers/query/${approval.query_pk}`, head),
        fetchTaskDetail(approval.query_pk),
      ]);
      setProjection(projectionResult.data?.data ?? null);
      setTaskDetail(taskResult);
    } catch (error) {
      setLoadError(error?.response?.data?.message || error.message || "Unable to load handover details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setDecision(null);
    setReason("");
    setApprovedNeedsApply(approval?.status === "approved" && isHandover);
    setProjection(null);
    setTaskDetail(null);
    if (isHandover) loadHandover();
  }, [open, approval?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const customer = taskDetail?.task?.customer ?? taskDetail?.customer ?? {};
  const partsById = useMemo(() => new Map((projection?.lines ?? taskDetail?.parts ?? []).map(line => [String(line.query_part_id ?? line.id), line])), [projection, taskDetail]);
  const handoverLines = payload.lines ?? projection?.lines ?? [];
  const requestedOwner = payload.requested_owner_id ? (userMap?.[payload.requested_owner_id] ?? `User #${payload.requested_owner_id}`) : "Automatic round-robin assignment";

  const applyHandover = async () => {
    const handoverId = payload.handover_id ?? projection?.handover?.id;
    if (!handoverId) throw new Error("The request does not contain a handover ID.");
    return axios.post(`${API_DOMAIN}/operations-v2/handovers/${handoverId}/apply-approved`, {}, head);
  };

  const submitDecision = async () => {
    if (!decision) return;
    if (decision !== "approve" && !reason.trim()) {
      message.warning("Please enter a reason.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_DOMAIN}/approval-requests/${approval.id}/action`, {
        userId,
        action: decision,
        decision_reason: reason.trim() || null,
      }, head);

      if (decision === "approve" && isHandover) {
        try {
          await applyHandover();
          message.success("Handover approved and transferred to Operations.");
        } catch (applyError) {
          setApprovedNeedsApply(true);
          message.warning(applyError?.response?.data?.message || "Approved, but the Operations handover could not be applied. Retry from this review.");
          onDone?.("approved", approval.id, { keepOpen: true });
          return;
        }
      } else {
        message.success(decision === "reject" ? "Operations request rejected." : "Changes requested from the submitter.");
      }

      const nextStatus = statusForAction(decision);
      onDone?.(nextStatus, approval.id);
      onClose();
    } catch (error) {
      message.error(error?.response?.data?.message || "The approval decision failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const retryApply = async () => {
    setSubmitting(true);
    try {
      await applyHandover();
      message.success("Approved handover transferred to Operations.");
      setApprovedNeedsApply(false);
      onDone?.("approved", approval.id);
      onClose();
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || "Unable to apply the approved handover.");
    } finally { setSubmitting(false); }
  };

  if (!approval) return null;

  return (
    <Modal
      open={open}
      title={<Space><ApartmentOutlined /><span>Review {meta.label}</span><Tag color={meta.color}>#{approval.query_id || approval.query_pk}</Tag></Space>}
      onCancel={onClose}
      width={isHandover ? 980 : 760}
      footer={null}
      destroyOnHidden
      zIndex={1350}
      styles={{ body: { maxHeight: "78vh", overflowY: "auto" } }}
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Alert
          type={approvedNeedsApply ? "warning" : "info"}
          showIcon
          message={approvedNeedsApply ? "Approved, but not yet transferred to Operations" : "Review the operational and financial exposure before deciding"}
          description={approvedNeedsApply ? "The approval decision is saved. Retry the handover application; do not approve the request again." : "The submitted snapshot is read-only. Use Request Changes when the Sales agent must correct it."}
        />

        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Request">#{approval.id}</Descriptions.Item>
          <Descriptions.Item label="Submitted by">{userMap?.[approval.submitted_by] ?? `User #${approval.submitted_by}`}</Descriptions.Item>
          <Descriptions.Item label="Required power">Priority {approval.required_priority ?? "—"}</Descriptions.Item>
        </Descriptions>

        {isHandover ? (
          loading ? <div style={{ padding: 40, textAlign: "center" }}><Spin /></div> : (
            <>
              {loadError && <Alert type="error" showIcon message="Live handover details unavailable" description={loadError} action={<Button icon={<ReloadOutlined />} onClick={loadHandover}>Retry</Button>} />}
              <Card size="small" title="Customer and authorization">
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label="Customer">{customer.customer_real_name || customer.customer_name || `Customer #${customer.customer_id || "—"}`}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{customer.customer_mobile || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Email">{customer.customer_email || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Basis"><Tag color="blue">{humanize(payload.authorization_basis)}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Order priority">{payload.priority ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Initial owner">{requestedOwner}</Descriptions.Item>
                  <Descriptions.Item label="Authorization terms" span={3}>{projection?.handover?.authorization_terms?.note || "No additional terms"}</Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title="Financial exposure">
                <Descriptions bordered size="small" column={3}>
                  <Descriptions.Item label="Locked invoice">{money(payload.invoice_total)}</Descriptions.Item>
                  <Descriptions.Item label="Received">{money(payload.received_amount)}</Descriptions.Item>
                  <Descriptions.Item label="Outstanding due"><Typography.Text type={Number(payload.due_amount || 0) > 0 ? "danger" : "success"} strong>{money(payload.due_amount)}</Typography.Text></Descriptions.Item>
                </Descriptions>
              </Card>

              <Card size="small" title={`Fulfilment lines (${handoverLines.length})`}>
                <List
                  dataSource={handoverLines}
                  locale={{ emptyText: "No fulfilment lines in this request" }}
                  renderItem={(line, index) => {
                    const liveLine = partsById.get(String(line.query_part_id)) ?? line;
                    const address = line.address ?? liveLine;
                    return (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Tag color="geekblue">{index + 1}</Tag>}
                          title={<Space wrap><Typography.Text strong>{liveLine.part_name || `Part #${line.query_part_id}`}</Typography.Text>{liveLine.part_number && <Tag>{liveLine.part_number}</Tag>}<Tag>Qty {liveLine.quantity ?? "—"}</Tag></Space>}
                          description={<><div>{addressText(address)}</div>{address.number && <div>{address.number}{address.pref_time ? ` · Preferred: ${address.pref_time}` : ""}</div>}{address.note && <div>Note: {address.note}</div>}</>}
                        />
                      </List.Item>
                    );
                  }}
                />
              </Card>
            </>
          )
        ) : <PayloadDetails actionKey={approval.action_key} payload={payload} />}

        {approval.status === "pending" && !approvedNeedsApply && (
          <>
            <Divider style={{ margin: "2px 0" }} />
            <div>
              <Typography.Text strong>Decision note</Typography.Text>
              <Typography.Paragraph type="secondary" style={{ margin: "2px 0 8px" }}>Optional for approval; required when rejecting or requesting changes.</Typography.Paragraph>
              <Input.TextArea rows={3} value={reason} onChange={event => setReason(event.target.value)} placeholder="Record the reason or instructions for the submitter" />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <Button icon={<UndoOutlined />} onClick={() => setDecision("request_changes")} type={decision === "request_changes" ? "primary" : "default"}>Request Changes</Button>
              <Button danger icon={<CloseCircleOutlined />} onClick={() => setDecision("reject")} type={decision === "reject" ? "primary" : "default"}>Reject</Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setDecision("approve")}>{isHandover ? "Approve & Transfer" : "Approve"}</Button>
              {decision && <Button type="primary" danger={decision === "reject"} loading={submitting} icon={<ExclamationCircleOutlined />} onClick={submitDecision}>Confirm {humanize(decision)}</Button>}
            </div>
          </>
        )}

        {approvedNeedsApply && <div style={{ display: "flex", justifyContent: "flex-end" }}><Button type="primary" loading={submitting} icon={<ReloadOutlined />} onClick={retryApply}>Retry Operations Transfer</Button></div>}
      </Space>
    </Modal>
  );
}
